import { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Upload, FileText, Check, X, AlertTriangle, Plus, Loader2, Sparkles, ExternalLink } from "lucide-react";

const CATEGORIES = ["Dry Goods", "Dairy", "Produce", "Meat & Seafood", "Beverages", "Coffee", "Bakery", "Packaging", "Cleaning", "Consumables"];
const BASE_UOMS = ["kg", "g", "L", "ml", "pc"];

export default function PdfImport({ items, locations, onDone }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [location, setLocation] = useState(locations[0]?.code || "CENTRAL");
  const [busy, setBusy] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [resolveSku, setResolveSku] = useState("");
  const [creatingItem, setCreatingItem] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", category: "Dry Goods", base_uom: "kg", purchase_uom_label: "", base_per_purchase: "" });
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("File exceeds 20 MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      const { data } = await api.post("/staff/inventory/v2/import/pdf/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`Extracted ${data.bill.lines.length} line${data.bill.lines.length === 1 ? "" : "s"} via ${data.extraction_source === "text_layer" ? "text layer" : "AI vision"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally { setUploading(false); }
  };

  const updateLine = (idx, patch) => {
    setPreview(prev => ({
      ...prev,
      bill: { ...prev.bill, lines: prev.bill.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) },
    }));
  };

  const resolveLine = async (idx) => {
    if (!resolveSku) { toast.error("Pick an item"); return; }
    const item = items.find(i => i.sku === resolveSku);
    if (!item) return;
    updateLine(idx, { resolved_sku: resolveSku, match_source: "manual", match_confidence: 1.0, line_status: "READY", status_detail: null });
    // Save alias for future imports
    try {
      await api.post("/staff/inventory/v2/item-aliases", {
        alias_text: preview.bill.lines[idx].description,
        sku: resolveSku,
        vendor: preview.bill.vendor_name || "",
      });
    } catch { /* alias save is best-effort */ }
    setResolving(null);
    setResolveSku("");
    toast.success(`Mapped to ${item.name}`);
  };

  const createItemInline = async (idx) => {
    if (!newItem.name.trim()) { toast.error("Item name required"); return; }
    setBusy(true);
    try {
      const { data: created } = await api.post("/staff/inventory/v2/items", {
        ...newItem,
        base_per_purchase: newItem.base_per_purchase ? Number(newItem.base_per_purchase) : null,
      });
      updateLine(idx, { resolved_sku: created.sku, match_source: "created", match_confidence: 1.0, line_status: "READY", status_detail: null });
      // Save alias
      await api.post("/staff/inventory/v2/item-aliases", {
        alias_text: preview.bill.lines[idx].description,
        sku: created.sku,
        vendor: preview.bill.vendor_name || "",
      });
      toast.success(`Created ${created.sku}`);
      setCreatingItem(null);
      setNewItem({ name: "", category: "Dry Goods", base_uom: "kg", purchase_uom_label: "", base_per_purchase: "" });
      onDone();  // refresh items list
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    if (!preview) return;
    const readyCount = preview.bill.lines.filter(l => l.line_status === "READY").length;
    if (readyCount === 0) { toast.error("No READY lines to import"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/staff/inventory/v2/import/pdf/confirm", {
        file_sha256: preview.file_sha256,
        filename: preview.filename,
        file_path: preview.file_path,
        bill: preview.bill,
        location,
        movement_type: "PURCHASE_GRN",
      });
      toast.success(`Draft batch ${data.batch_no} created — ${data.lines.length} lines`);
      setPreview(null);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const autoSeedItems = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staff/inventory/v2/import/pdf/seed-items", { bill: preview.bill });
      setPreview(prev => ({ ...prev, bill: { ...prev.bill, lines: data.lines } }));
      toast.success(`Created ${data.created} new item${data.created === 1 ? "" : "s"} · matched ${data.matched} existing · ${data.aliases_saved} aliases saved`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-create failed");
    } finally { setBusy(false); }
  };

  const readyCount = preview?.bill?.lines?.filter(l => l.line_status === "READY").length || 0;
  const totalCount = preview?.bill?.lines?.length || 0;
  const unmatchedCount = totalCount - readyCount;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="pdf-import" className="space-y-4">
      {!preview && (
        <>
          <div className="p-4 border border-[hsl(var(--brass))] bg-[hsl(38_90%_96%)] flex items-start gap-3">
            <Sparkles size={18} className="text-[hsl(var(--brass-2))] mt-0.5 shrink-0" />
            <div className="text-xs text-[hsl(var(--ink-soft))]">
              <b>PDF & Photo bills.</b> Upload a vendor invoice — we try the PDF text layer first, then AI vision as a fallback.
              You'll get every line back with qty, rate, amount and GST rate to review before posting.
            </div>
          </div>
          <div
            data-testid="pdf-dropzone"
            onClick={() => fileRef.current?.click()}
            className="p-8 border-2 border-dashed border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center cursor-pointer hover:border-[hsl(var(--forest))] transition-colors"
          >
            <input ref={fileRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp,image/heic" onChange={handleFile} className="hidden" />
            {uploading ? (
              <>
                <Loader2 size={32} className="mx-auto animate-spin text-[hsl(var(--ink-soft))]" />
                <div className="mt-2 text-sm text-[hsl(var(--ink-soft))]">Extracting… this may take 10-30s for photos</div>
              </>
            ) : (
              <>
                <FileText size={32} className="mx-auto text-[hsl(var(--ink-soft))]" />
                <div className="mt-2 font-semibold">Drop bill PDF or photo</div>
                <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">.pdf, .jpg, .png, .webp, .heic — max 20 MB</div>
              </>
            )}
          </div>
        </>
      )}

      {preview && (
        <div className="space-y-4">
          {/* Header + totals */}
          <div className="p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))]">
                  Extracted via {preview.extraction_source === "text_layer" ? "PDF text layer" : "AI vision"}
                </div>
                <div className="mt-1 font-semibold">{preview.bill.vendor_name || "Unknown vendor"}</div>
                <div className="text-xs text-[hsl(var(--ink-soft))]">
                  {preview.bill.gstin && `GSTIN: ${preview.bill.gstin} · `}
                  {preview.bill.invoice_no && `Invoice: ${preview.bill.invoice_no} · `}
                  {preview.bill.invoice_date}
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[hsl(var(--ink-soft))]"><X size={16} /></button>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">Subtotal</div>
                <div className="font-semibold">₹{(preview.bill.subtotal || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">Tax</div>
                <div className="font-semibold">₹{(preview.bill.tax || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">Grand total</div>
                <div className="font-semibold">₹{(preview.bill.grand_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
              </div>
            </div>
            {preview.total_check.mismatches.length > 0 && (
              <div className="mt-3 p-2 bg-[hsl(38_90%_92%)] text-[10px] text-[hsl(35_75%_38%)] flex items-start gap-1">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                <div>{preview.total_check.mismatches.map((m, i) => <div key={i}>{m}</div>)}</div>
              </div>
            )}
          </div>

          {/* Location picker */}
          <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Deliver to
            <select data-testid="pdf-location" value={location} onChange={e => setLocation(e.target.value)} className="tbb-inp mt-1 w-full">
              {locations.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </label>

          {/* Source file link */}
          <a href={`${backendUrl}/api/staff/inventory/v2/uploads/download?path=${encodeURIComponent(preview.file_path)}`}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-xs text-[hsl(var(--forest))] hover:underline">
            <ExternalLink size={12} /> View original bill
          </a>

          {/* Review grid */}
          {unmatchedCount > 0 && (
            <button data-testid="pdf-autocreate-items" onClick={autoSeedItems} disabled={busy}
                    className="w-full p-3 border border-[hsl(var(--forest))] text-[hsl(var(--forest))] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              Auto-create items for {unmatchedCount} unmatched line{unmatchedCount === 1 ? "" : "s"}
            </button>
          )}
          <div className="border border-[hsl(var(--rule))] divide-y divide-[hsl(var(--rule))]">
            <div className="p-2 bg-[hsl(var(--bg-2))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] flex justify-between">
              <span>{totalCount} line{totalCount === 1 ? "" : "s"} · {readyCount} ready</span>
              <span className="text-[hsl(var(--brass-2))]">₹{preview.total_check.lines_sum.toLocaleString("en-IN")}</span>
            </div>
            {preview.bill.lines.map((l, i) => {
              const isReady = l.line_status === "READY";
              const item = l.resolved_sku ? items.find(it => it.sku === l.resolved_sku) : null;
              return (
                <div key={i} data-testid={`pdf-line-${i}`} className={`p-3 ${isReady ? "" : "bg-[hsl(38_90%_96%)]"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{l.description}</div>
                      <div className="text-xs text-[hsl(var(--ink-soft))] mt-0.5">
                        {l.qty} {l.uom || ""} @ ₹{l.rate} = <b>₹{l.amount}</b>
                        {l.hsn && ` · HSN ${l.hsn}`}
                        {l.gst_rate && ` · GST ${(l.gst_rate * 100).toFixed(1)}%`}
                      </div>
                      {isReady && item && (
                        <div className="text-xs text-[hsl(var(--forest))] mt-1">
                          → {item.name} ({item.sku})
                          {l.match_source === "fuzzy" && ` · ${(l.match_confidence * 100).toFixed(0)}% fuzzy`}
                          {l.match_source === "alias" && ` · alias`}
                          {l.match_source === "manual" && ` · manually mapped`}
                          {l.match_source === "created" && ` · newly created`}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${isReady ? "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]" : "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]"}`}>
                        {isReady ? "READY" : "UNMATCHED"}
                      </span>
                      {!isReady && resolving !== i && creatingItem !== i && (
                        <div className="flex gap-1">
                          <button data-testid={`pdf-map-${i}`} onClick={() => setResolving(i)} className="text-xs px-2 py-1 border border-[hsl(var(--forest))] text-[hsl(var(--forest))]">
                            Map
                          </button>
                          <button data-testid={`pdf-new-${i}`} onClick={() => { setCreatingItem(i); setNewItem({ ...newItem, name: l.description }); }} className="text-xs px-2 py-1 border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))]">
                            <Plus size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {resolving === i && (
                    <div className="mt-3 p-3 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]">
                      <select value={resolveSku} onChange={e => setResolveSku(e.target.value)} className="tbb-inp w-full text-sm">
                        <option value="">— Select item —</option>
                        {items.map(it => <option key={it.sku} value={it.sku}>{it.name} ({it.sku})</option>)}
                      </select>
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => resolveLine(i)} disabled={!resolveSku} className="text-xs px-3 py-1.5 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] disabled:opacity-50">Resolve</button>
                        <button onClick={() => { setResolving(null); setResolveSku(""); }} className="text-xs px-3 py-1.5 text-[hsl(var(--ink-soft))]">Cancel</button>
                      </div>
                    </div>
                  )}
                  {creatingItem === i && (
                    <div className="mt-3 p-3 border border-[hsl(var(--brass))] bg-[hsl(var(--bg))]">
                      <input value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} placeholder="Item name" className="tbb-inp w-full text-sm mb-2" />
                      <div className="grid grid-cols-2 gap-2">
                        <select value={newItem.category} onChange={e => setNewItem({ ...newItem, category: e.target.value })} className="tbb-inp text-sm">
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select value={newItem.base_uom} onChange={e => setNewItem({ ...newItem, base_uom: e.target.value })} className="tbb-inp text-sm">
                          {BASE_UOMS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <input value={newItem.purchase_uom_label} onChange={e => setNewItem({ ...newItem, purchase_uom_label: e.target.value })} placeholder="Pack label" className="tbb-inp text-sm" />
                        <input type="number" value={newItem.base_per_purchase} onChange={e => setNewItem({ ...newItem, base_per_purchase: e.target.value })} placeholder="Base per pack" className="tbb-inp text-sm" />
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => createItemInline(i)} disabled={busy} className="text-xs px-3 py-1.5 bg-[hsl(var(--brass))] text-[hsl(var(--bg))] disabled:opacity-50">
                          {busy ? "Creating..." : "Create & map"}
                        </button>
                        <button onClick={() => setCreatingItem(null)} className="text-xs px-3 py-1.5 text-[hsl(var(--ink-soft))]">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button data-testid="pdf-confirm" onClick={confirmImport} disabled={busy || readyCount === 0} className="btn-brass !py-3 !px-6 text-sm w-full disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin inline" /> : `Import ${readyCount} ready line${readyCount === 1 ? "" : "s"} as draft batch`}
          </button>
          {totalCount > readyCount && (
            <div className="text-xs text-center text-[hsl(35_75%_38%)]">
              {totalCount - readyCount} unmatched line{totalCount - readyCount === 1 ? "" : "s"} will be skipped
            </div>
          )}
        </div>
      )}

      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.4rem 0.6rem;font-size:0.85rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
